import React from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { Database } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

const MAX_EMAIL_LENGTH = 254

/** Validates email format without regex to avoid ReDoS. */
function isValidEmail(s: string): boolean {
  const t = s.trim()
  if (t.length === 0 || t.length > MAX_EMAIL_LENGTH) return false
  const at = t.indexOf('@')
  if (at <= 0 || at === t.length - 1) return false
  const local = t.slice(0, at)
  const domain = t.slice(at + 1)
  if (/\s/.test(local) || /\s/.test(domain)) return false
  const lastDot = domain.lastIndexOf('.')
  if (lastDot <= 0 || lastDot === domain.length - 1) return false
  return domain.length >= 3
}

export function LoginPage() {
  const navigate = useNavigate()
  const { user, loading, login } = useAuth()
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [error, setError] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [touched, setTouched] = React.useState({
    email: false,
    password: false,
  })

  const emailValid = email.trim() ? isValidEmail(email.trim()) : false
  const passwordValid = password.length > 0
  let emailError: string | null = null
  if (touched.email && !email.trim()) emailError = 'Email is required'
  else if (touched.email && !emailValid)
    emailError = 'Enter a valid email address'
  const passwordError =
    touched.password && !passwordValid ? 'Password is required' : null

  const handleSubmit = async (e: React.BaseSyntheticEvent) => {
    e.preventDefault()
    setError('')
    setTouched({ email: true, password: true })
    if (!email.trim() || !emailValid || !passwordValid) return
    setSubmitting(true)
    try {
      const result = await login(email.trim(), password)
      if (result.ok) {
        navigate('/', { replace: true })
      } else {
        setError(result.error ?? 'Login failed')
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (!loading && user) {
    return <Navigate to='/dashboard' replace />
  }

  return (
    <div className='min-h-screen flex flex-col items-center justify-center bg-background text-foreground font-sans overflow-hidden relative p-4 sm:p-6'>
      <div className='absolute inset-0 bg-gradient-to-tr from-blue-100/60 via-background to-purple-100/60 pointer-events-none' />

      <div className='w-full max-w-md mx-auto relative z-10'>
        <div className='flex items-center justify-center gap-3 mb-8 sm:mb-10'>
          <div className='h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/20'>
            <Database className='text-white h-6 w-6 sm:h-7 sm:w-7' />
          </div>
          <span className='font-bold text-xl sm:text-2xl tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600'>
            DataMatch
          </span>
        </div>

        <div className='rounded-2xl border border-border bg-card p-5 sm:p-8 shadow-xl shadow-black/10'>
          <h1 className='text-xl font-bold mb-1 bg-clip-text text-transparent bg-gradient-to-r from-foreground to-muted-foreground'>
            Sign in
          </h1>
          <p className='text-muted-foreground text-sm mb-6'>
            Welcome back. Sign in to continue.
          </p>

          <form onSubmit={handleSubmit} className='space-y-5'>
            {error && (
              <div className='rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm px-4 py-3'>
                {error}
              </div>
            )}

            <div>
              <label
                htmlFor='email'
                className='block text-sm font-medium text-muted-foreground mb-2'
              >
                Email <span className='text-red-500'>*</span>
              </label>
              <input
                id='email'
                type='email'
                autoComplete='email'
                value={email}
                onChange={e => setEmail(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, email: true }))}
                required
                className={`login-input w-full rounded-lg border bg-muted/60 px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                  emailError
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30'
                    : 'border-border focus:border-primary/30'
                }`}
              />
              {emailError && (
                <p className='mt-1.5 text-sm text-red-500' role='alert'>
                  {emailError}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor='password'
                className='block text-sm font-medium text-muted-foreground mb-2'
              >
                Password <span className='text-red-500'>*</span>
              </label>
              <input
                id='password'
                type='password'
                autoComplete='current-password'
                value={password}
                onChange={e => setPassword(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, password: true }))}
                required
                className={`login-input w-full rounded-lg border bg-muted/60 px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                  passwordError
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30'
                    : 'border-border focus:border-primary/30'
                }`}
              />
              {passwordError && (
                <p className='mt-1.5 text-sm text-red-500' role='alert'>
                  {passwordError}
                </p>
              )}
            </div>

            <button
              type='submit'
              disabled={submitting}
              className='w-full px-6 py-3 rounded-full font-bold text-white shadow-lg transition-all bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed'
            >
              {submitting ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
